// src/ResetPassword.js
import React, { useState, useEffect } from "react";
import {
  onAuthStateChanged,
  getAuth,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "./FirebaseConfig.js";
import { Navigate, Link } from "react-router-dom";
import {
  Input,
  Button,
  Form,
  message,
  Spin,
  Card,
  Typography,
  Space,
  Alert,
} from "antd";
import { UserOutlined, ExportOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const ResetPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [sent, setSent] = useState(false);

  // ログイン状態監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // 送信処理
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await sendPasswordResetEmail(getAuth(), email);
      setSent(true);
      message.success(t("resetPassword.success", { email }));
    } catch {
      message.error(t("resetPassword.error"));
    } finally {
      setLoading(false);
    }
  };

  // ログイン済みはトップへ
  if (user) return <Navigate to="/" />;

  if (loading) {
    return (
      <div className="center-screen">
        <Spin size="large" tip={t("resetPassword.loading")} />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #f0f5ff 0%, #ffffff 60%)",
      }}
    >
      <Card
        style={{
          width: 420,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        }}
        bodyStyle={{ padding: 32 }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {/* タイトル */}
          <div style={{ textAlign: "center" }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              {t("resetPassword.title")}
            </Title>
            <Text type="secondary">
              {t("resetPassword.subtitle", {
                defaultValue:
                  "Enter your registered email address and we’ll send you a password reset link.",
              })}
            </Text>
          </div>

          {/* 送信完了メッセージ */}
          {sent && (
            <Alert
              type="success"
              showIcon
              message={t("resetPassword.sentTitle", {
                defaultValue: "Reset email sent",
              })}
              description={t("resetPassword.sentDesc", {
                defaultValue:
                  "Please check your inbox and follow the instructions to reset your password.",
              })}
            />
          )}

          {/* フォーム */}
          {!sent && (
            <Form layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label={t("resetPassword.label")}
                name="email"
                rules={[
                  {
                    type: "email",
                    required: true,
                    message: t("resetPassword.errors.required"),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  size="large"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                icon={<ExportOutlined />}
              >
                {t("resetPassword.sendBtn")}
              </Button>
            </Form>
          )}

          {/* 戻る */}
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <Link to="/login">
              <ArrowLeftOutlined /> {t("resetPassword.loginLink")}
            </Link>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ResetPassword;
